import React, { Component } from 'react';
import i18n from '../../../../i18n';
import { getActiveAuctions } from '../../../../models/auctions';
import '../../../../styles/components/auction-map.scss';

// CDN links — add these to your public/index.html <head> if not already:
// <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css" />
// <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/leaflet.markercluster/1.5.3/MarkerCluster.css" />
// <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/leaflet.markercluster/1.5.3/MarkerCluster.Default.css" />
// <script src="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js"></script>
// <script src="https://cdnjs.cloudflare.com/ajax/libs/leaflet.markercluster/1.5.3/leaflet.markercluster.min.js"></script>

const DEFAULT_CENTER = [23.5880, 58.3829]; // Muscat, Oman
const DEFAULT_ZOOM = 10;

class AuctionMap extends Component {
    constructor(props) {
        super(props);
        this.state = {
            auctions: [],
            loading: true,
            selectedAuction: null,
        };
        this.mapRef = React.createRef();
        this.map = null;
        this.clusterGroup = null;
    }

    componentDidMount() {
        this.initMap();
        this.loadAuctions();
    }

    componentWillUnmount() {
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    }

    initMap() {
        const L = window.L;
        if (!L) {
            console.error('Leaflet not loaded. Add leaflet CDN to index.html');
            return;
        }

        this.map = L.map(this.mapRef.current, {
            center: DEFAULT_CENTER,
            zoom: DEFAULT_ZOOM,
            zoomControl: false,
        });

        // Tile layer — clean style
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors',
            maxZoom: 18,
        }).addTo(this.map);

        // Zoom control — bottom right
        L.control.zoom({ position: 'bottomright' }).addTo(this.map);

        // Cluster group
        this.clusterGroup = L.markerClusterGroup({
            showCoverageOnHover: false,
            maxClusterRadius: 60,
            spiderfyOnMaxZoom: true,
            iconCreateFunction: (cluster) => {
                const count = cluster.getChildCount();
                let size = 'am-cluster--sm';
                if (count >= 50) size = 'am-cluster--lg';
                else if (count >= 10) size = 'am-cluster--md';

                return L.divIcon({
                    html: `<div class="am-cluster ${size}"><span>${count}</span></div>`,
                    className: 'am-cluster-wrapper',
                    iconSize: L.point(44, 44),
                });
            },
        });

        this.map.addLayer(this.clusterGroup);
    }

    async loadAuctions() {
        this.setState({ loading: true });
        try {
            const response = await getActiveAuctions('limit=30');
            const allAuctions = response?.data || [];

            // Filter auctions that have valid coordinates
            const withCoords = allAuctions.filter(
                (a) => a.latitude && a.longitude && !isNaN(parseFloat(a.latitude)) && !isNaN(parseFloat(a.longitude))
            );

            this.setState({ auctions: withCoords, loading: false }, () => {
                this.plotMarkers(withCoords);
            });
        } catch (e) {
            console.error('Failed to load auctions for map:', e);
            this.setState({ loading: false });
        }
    }

    plotMarkers(auctions) {
        const L = window.L;
        if (!L || !this.clusterGroup) return;

        this.clusterGroup.clearLayers();

        const current_lng = localStorage.getItem('I18N_LANGUAGE');

        auctions.forEach((auction) => {
            const lat = parseFloat(auction.latitude);
            const lng = parseFloat(auction.longitude);
            const title = current_lng === 'ar' ? (auction.title_ar || auction.title) : auction.title;
            const image = auction.images?.[0]?.image || auction.image || '';

            const customIcon = L.divIcon({
                html: `<div class="am-pin"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg></div>`,
                className: 'am-pin-wrapper',
                iconSize: L.point(32, 32),
                iconAnchor: L.point(16, 32),
                popupAnchor: L.point(0, -34),
            });

            const popupContent = `
                <div class="am-popup">
                    ${image ? `<div class="am-popup__img-wrap"><img src="${image}" alt="" class="am-popup__img" /></div>` : ''}
                    <div class="am-popup__body">
                        <div class="am-popup__id">MZAD${auction.id}</div>
                        <div class="am-popup__title">${title || ''}</div>
                        <div class="am-popup__meta">
                            ${auction.location || auction.location_ar ? `<span class="am-popup__loc"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg> ${current_lng === 'ar' ? (auction.location_ar || auction.location) : (auction.location || auction.location_ar)}</span>` : ''}
                            ${auction.current_amount ? `<span class="am-popup__price">${Number(auction.current_amount).toLocaleString()} OMR</span>` : ''}
                        </div>
                        <a href="/auction-details/${auction.id}" class="am-popup__btn">${i18n.t('view_details') || 'View Details'}</a>
                    </div>
                </div>
            `;

            const marker = L.marker([lat, lng], { icon: customIcon });
            marker.bindPopup(popupContent, {
                maxWidth: 280,
                minWidth: 220,
                className: 'am-popup-container',
            });

            this.clusterGroup.addLayer(marker);
        });

        // Fit bounds if markers exist
        if (auctions.length > 0) {
            const bounds = this.clusterGroup.getBounds();
            if (bounds.isValid()) {
                this.map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
            }
        }
    }

    render() {
        const { loading, auctions } = this.state;

        return (
            <div className="am-page">
                {/* Map container */}
                <div className="am-map" ref={this.mapRef} />

                {/* Loading overlay */}
                {loading && (
                    <div className="am-loading">
                        <div className="am-loading__spinner" />
                        <span>{i18n.t('loading_map') || 'Loading map...'}</span>
                    </div>
                )}

                {/* Count badge */}
                {!loading && (
                    <div className="am-count-badge">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                            <circle cx="12" cy="10" r="3" />
                        </svg>
                        <span>{auctions.length} {i18n.t('auctions') || 'Auctions'}</span>
                    </div>
                )}
            </div>
        );
    }
}

export default AuctionMap;